import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Sparkles, Info, CircleCheck, TriangleAlert } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useNotificationStore } from "@/store/notificationStore";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const KIND_ICON: Record<string, any> = { info: Info, success: CircleCheck, warning: TriangleAlert, ai: Sparkles };
const KIND_COLOR: Record<string, string> = {
  info: "text-blue-500",
  success: "text-emerald-500",
  warning: "text-amber-500",
  ai: "text-ai",
};

export function NotificationBell() {
  const { items, unread, markRead, markAll } = useNotificationStore();
  const navigate = useNavigate();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative h-9 w-9 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
        >
          <Bell className="h-[18px] w-[18px]" />
          {unread > 0 && (
            <span
              data-testid="notification-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-ai text-white text-[10px] font-bold flex items-center justify-center"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="text-sm font-semibold">Notifications</span>
          {unread > 0 && (
            <button
              data-testid="notification-mark-all"
              onClick={() => markAll()}
              className="text-xs text-ai font-medium inline-flex items-center gap-1 hover:underline"
            >
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">You're all caught up.</div>
          )}
          {items.map((n) => {
            const Icon = KIND_ICON[n.kind] || Info;
            return (
              <button
                key={n.id}
                data-testid={`notification-item-${n.id}`}
                onClick={() => {
                  if (!n.read) markRead(n.id);
                  if (n.link) navigate(n.link);
                }}
                className={`w-full text-left flex gap-3 px-4 py-3 border-b last:border-b-0 hover:bg-accent transition-colors ${
                  !n.read ? "bg-accent/40" : ""
                }`}
              >
                <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${KIND_COLOR[n.kind] || "text-muted-foreground"}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{n.title}</div>
                  {n.body && <div className="text-xs text-muted-foreground line-clamp-2">{n.body}</div>}
                  <div className="text-[10px] text-muted-foreground mt-1">{dayjs(n.at).fromNow()}</div>
                </div>
                {!n.read && <span className="h-2 w-2 rounded-full bg-ai mt-1.5 shrink-0" />}
              </button>
            );
          })}
        </div>
        <button
          onClick={() => navigate("/notifications")}
          className="w-full text-center text-xs font-medium py-2.5 border-t text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        >
          View all
        </button>
      </PopoverContent>
    </Popover>
  );
}

export default NotificationBell;
